import { useState, type FormEvent } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import { Button, Input, Label } from '@/components/ui'
import { ApiError, post } from '@/lib/api'
import { qk } from '@/lib/query'
import { AuthCard, FormError } from './AuthCard'

export function LoginPage() {
  const nav = useNavigate()
  const qc = useQueryClient()
  const search = useSearch({ strict: false }) as { next?: string }
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function submit(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await post('/auth/login', { email, password })
      await qc.invalidateQueries({ queryKey: qk.me() })
      // Login never yields a full session: 2FA always comes next (01 §6).
      await nav({ to: '/login/2fa', search: { next: search.next } })
    } catch (err) {
      // One generic line whatever went wrong: no account enumeration (01 §3.1).
      setError(err instanceof ApiError && err.status === 429 ? 'Too many attempts. Wait a few minutes and try again.' : err instanceof ApiError && err.status === 0 ? err.what : 'Email or password is wrong.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <AuthCard footer="Invite-only. Ask an admin for an account.">
      <form onSubmit={submit}>
        <FormError>{error}</FormError>
        <Label htmlFor="email">Email</Label>
        <Input id="email" type="email" autoComplete="username webauthn" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" autoFocus />
        <Label htmlFor="password" className="mt-3">
          Password
        </Label>
        <Input id="password" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
        <Button type="submit" variant="primary" size="md" className="mt-4 w-full" disabled={busy}>
          {busy ? 'Signing in…' : 'Sign in'}
        </Button>
        <Link to="/forgot" className="mt-3 block text-xs text-fg-subtle hover:text-fg">
          Forgot password?
        </Link>
      </form>
    </AuthCard>
  )
}
